import axios from 'axios';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useLocation, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { Button, ErrorToast } from '../../components';
import Spinner from '../../components/common/Spinner';

import { removeUserFromLS } from '../../helpers/localstorage-helper';
import { DASHBOARD, LOGIN, REFERRAL, SECURITY_CODE } from '../../routes/routes';
import {
  continueToAffiliateTerms,
  logoutRemoveState,
  signIn,
  signInComplete
} from '../../slices/auth-slice';

import { CaptionText, P3 } from '../../styles/FontStyles';
import { AuthLayoutTitle, Flexed } from '../../styles/GlobalComponents';
import { colors } from '../../styles/GlobalStyles';

import { InputBackOffice } from '../../components/common/InputBackoffice';

const captchaId = process.env.REACT_APP_CAPTCHA_ID;
const API_URL = process.env.REACT_APP_API_URL;

type TParams = { id?: string; referralCode?: string };

const Login = ({ history, match }: RouteComponentProps<TParams>) => {
  const { t } = useTranslation('onboarding');

  const [referrerId, setReferrerId] = useState(Number(match.params.id) ?? null);
  const [isWithReferral, setIsWithReferral] = useState(true);

  const [captchaLoading, setCaptchaLoading] = useState(false);

  const [captchaResult, setCaptchaResult] = useState<any>();
  const [walletAddress, setWalletAddress] = useState<string>('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [waitingApproval, setWaitingApproval] = useState(false);

  const eventSourceRef = useRef<EventSource | null>(null);

  const dispatch = useAppDispatch();
  let { referralCode }: any = useParams();
  referralCode = referralCode ?? null;

  const { loading } = useAppSelector((state) => state.auth);

  const location = useLocation();

  const onHandleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!walletAddress) {
      ErrorToast('Please enter your VeilOn wallet address');
    }
    // else if (!validateEmail(email)) {
    //   ErrorToast(t('login.error_email_invalid'));
    //   return;
    // }
    else {
      setCaptchaLoading(true);
      window?.initGeetest4(
        {
          captchaId,
          language: 'eng',
          product: 'bind',
          riskType: 'slide',
          nativeButton: {
            width: '100%',
            marginBottom: '20px'
          }
        },
        function (captcha: any) {
          captcha.showBox();
          captcha
            .onReady(function () {
              setCaptchaLoading(false);
            })
            .onSuccess(async function () {
              const result = captcha.getValidate();

              const res: any = await dispatch(
                signIn({
                  result,
                  walletAddress,
                  history
                })
              );

              if (res.meta.requestStatus === 'fulfilled') {
                setSessionId(res.payload);
                setWaitingApproval(true);
              }
            })
            .onError(function () {
              setCaptchaLoading(false);
            });
        }
      );
    }
  };

  useEffect(() => {
    if (location.pathname === DASHBOARD || location.pathname === LOGIN) {
      setIsWithReferral(false);
    } else {
      setIsWithReferral(true);
    }

    setReferrerIdByCodeIfAfiliateRegistration();

    removeUserFromLS();

    dispatch(logoutRemoveState());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location]);

  //  SSE LISTENER
  useEffect(() => {
    if (!sessionId) return;

    // // cleanup ako postoji stara konekcija
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const es = new EventSource(
      `${API_URL}/flipme-auth/signin/status?sessionId=${sessionId}`
    );

    const approvalTimeout = setTimeout(() => {
      console.warn('SSE timeout - user did not approve in time');
      es.close();
      setWaitingApproval(false);
      ErrorToast('Approval timeout. Please try again.');
    }, 60000);

    eventSourceRef.current = es;

    es.addEventListener('approved', (event: any) => {
      try {
        const parsed = JSON.parse(event.data);

        if (parsed.status === 'approved') {
          clearTimeout(approvalTimeout);
          es.close();
          setWaitingApproval(false);

          dispatch(
            signInComplete({
              signInToken: parsed?.signInToken,
              emailCode: null,
              twoFAcode: null,
              is2FARequired: parsed?.is2FARequired,
              history
            })
          );
        }
      } catch (err) {
        console.error('approved parse error', err);
      }
    });

    es.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);

        if (parsed.status === 'approved') {
          es.close();
          setWaitingApproval(false);
          dispatch(
            signInComplete({
              signInToken: parsed?.signInToken,
              emailCode: null,
              twoFAcode: null,
              is2FARequired: parsed?.is2FARequired,
              history
            })
          );
        }
      } catch (err) {
        console.error('message parse error', err);
      }
    };

    es.onerror = (err: any) => {
      clearTimeout(approvalTimeout);
      console.error('SSE error:', err);
      es.close();
      setWaitingApproval(false);

      if (err?.data) {
        try {
          const parsed = JSON.parse(err?.data);

          ErrorToast(parsed?.message);
        } catch (e) {
          ErrorToast('Connection lost. Please try again.');
        }
      } else {
        ErrorToast('Connection lost. Please try again.');
      }
    };

    return () => {
      clearTimeout(approvalTimeout);
      es.close();
    };
  }, [sessionId, history]);

  const setReferrerIdByCodeIfAfiliateRegistration = async () => {
    if (referralCode !== null) {
      const { data } = await axios.get(`/user/referral-code/${referralCode}`);

      setReferrerId(data.data.user.id);
    }
  };

  return (
    <div>
      {loading && <Spinner fullScreen={true} transparent />}
      {captchaLoading && <Spinner fullScreen={true} transparent />}
      {waitingApproval && <Spinner fullScreen={true} transparent />}

      {!isWithReferral && (
        <AuthLayoutTitle>Sign In With Your VeilOn Wallet</AuthLayoutTitle>
      )}

      {!isWithReferral && (
        <>
          <form onSubmit={onHandleSubmit}>
            <InputBackOffice
              id={'id'}
              type={'text'}
              placeholder={'Enter your VeilOn address'}
              name={'walletAddress'}
              value={walletAddress.replace(/\s+/g, '')}
              onChange={(e: any) => setWalletAddress(e.replace(/\s+/g, ''))}
              label={'VeilOn Wallet Address'}
              style={{ textTransform: 'lowercase' }}
            />

            <Button disabled={waitingApproval} m='10px 0 20px 0'>
              {waitingApproval ? 'Waiting for approval...' : 'Sign in'}
            </Button>
          </form>
          <Button
            m='10px 0 20px 0'
            negative
            onClick={() => history.push(REFERRAL)}>
            Create Account
          </Button>
        </>
      )}

      {isWithReferral && match.params.id && (
        <Button
          onClick={() => {
            dispatch(
              continueToAffiliateTerms({
                referrerId: parseInt(match.params.id || ''),
                history
              })
            );
          }}>
          {t('login.create_account')}
        </Button>
      )}

      {isWithReferral && (
        <CaptionText center m='16px 0 0 0'>
          {t('login.already_have_account')}{' '}
          <span
            style={{
              color: colors.primary900,
              cursor: 'pointer',
              fontWeight: 500
            }}
            onClick={() => history.push('/login')}>
            {t('login.sign_in')}
          </span>
        </CaptionText>
      )}
    </div>
  );
};

export default Login;
